import {
  AgentDigestV1SessionEvent,
  SessionEvent,
  SessionEventKind,
} from "@/mds/components/sessions/session.types";
import styled from "@emotion/styled";
import { findLast, findLastIndex } from "lodash-es";

/**
 * We get the latest digest AFTER the latest user message.
 *
 * (If there isn't one, we return undefined.)
 */
export const useLatestAgentDigestEventData = ({
  sessionEvents,
}: {
  sessionEvents: SessionEvent[];
}): AgentDigestV1SessionEvent | undefined => {
  const latestUserMessageIndex = findLastIndex(
    sessionEvents,
    e => e.data.kind === SessionEventKind.UserActionV1
  );

  if (latestUserMessageIndex === -1) {
    return undefined;
  }

  const latestAgentDigestEvent = findLast(
    sessionEvents.slice(latestUserMessageIndex + 1),
    e => e.data.kind === SessionEventKind.AgentDigestV1
  );

  if (!latestAgentDigestEvent) {
    return undefined;
  }

  return latestAgentDigestEvent.data as AgentDigestV1SessionEvent;
};

export const getAgentDigestEventContent = ({ content }: { content: string }) => {
  // Regular expression to match markdown links: [text](url)
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(content)) !== null) {
    const [fullMatch, linkText, _linkUrl] = match;
    const matchIndex = match.index;

    // Add text before the link
    if (matchIndex > lastIndex) {
      parts.push(content.slice(lastIndex, matchIndex));
    }

    // Add the styled link
    parts.push(<StyledLink key={`link-${matchIndex}`}>{linkText}</StyledLink>);

    lastIndex = matchIndex + fullMatch.length;
  }

  // Add remaining text after the last link
  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  // If no links were found, return the original content
  if (parts.length === 0) {
    return content;
  }

  return (
    <>
      {parts.map((part, index) => (
        <span key={index}>{part}</span>
      ))}
    </>
  );
};

const StyledLink = styled.span(({ theme }) => ({
  fontWeight: theme.fontWeights.semiBold,
}));
